import { useRef, useState } from 'react';

/**
 * Daily views trend - a compact area chart with a hover crosshair + tooltip.
 * Pure inline SVG (no chart library); colours come from the theme CSS vars so
 * it flips with Night/Day Souq. Expects a continuous, zero-filled series.
 */
export function ViewsTrend({ data }: { data: { day: string; views: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const n = data.length;
  if (!n) return null;
  const max = Math.max(...data.map((d) => d.views), 1);
  const total = data.reduce((a, d) => a + d.views, 0);
  const peak = Math.max(...data.map((d) => d.views), 0);

  const W = 600, H = 150, padX = 6, padTop = 14, padBottom = 6;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const x = (i: number) => padX + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => padTop + innerH - (v / max) * innerH;

  const linePts = data.map((d, i) => `${x(i)},${y(d.views)}`).join(' ');
  const areaPath = `M ${x(0)},${padTop + innerH} L ${data.map((d, i) => `${x(i)},${y(d.views)}`).join(' L ')} L ${x(n - 1)},${padTop + innerH} Z`;

  function onMove(e: React.MouseEvent) {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setHover(Math.round(frac * (n - 1)));
  }
  const fmt = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="mono text-[9px] tracking-[0.18em] uppercase text-mute">Views · last 30 days</div>
        <div className="text-[11px] text-mute"><span className="display text-base text-ink">{total.toLocaleString()}</span> total · peak {peak}</div>
      </div>
      <div ref={wrapRef} className="relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[150px] block" preserveAspectRatio="none">
          <defs>
            <linearGradient id="vt-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--c-gold))" stopOpacity="0.28" />
              <stop offset="100%" stopColor="rgb(var(--c-gold))" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <line x1={padX} y1={padTop + innerH} x2={W - padX} y2={padTop + innerH} stroke="rgb(var(--c-warmline))" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d={areaPath} fill="url(#vt-fill)" />
          <polyline points={linePts} fill="none" stroke="rgb(var(--c-gold))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          {hover !== null && (
            <>
              <line x1={x(hover)} y1={padTop} x2={x(hover)} y2={padTop + innerH} stroke="rgb(var(--c-gold-dark))" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              <circle cx={x(hover)} cy={y(data[hover].views)} r="3.5" fill="rgb(var(--c-gold))" stroke="rgb(var(--c-surface))" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            </>
          )}
        </svg>
        <div className="flex justify-between mono text-[9px] text-mute-soft mt-1 px-0.5">
          <span>{fmt(data[0].day)}</span>
          <span>{fmt(data[Math.floor(n / 2)].day)}</span>
          <span>{fmt(data[n - 1].day)}</span>
        </div>
        {hover !== null && (
          <div className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 -translate-y-full" style={{ left: `${(hover / Math.max(n - 1, 1)) * 100}%` }}>
            <div className="section-dark rounded-lg bg-ink text-cream px-2.5 py-1.5 shadow-luxe whitespace-nowrap">
              <div className="display text-sm leading-none">{data[hover].views} view{data[hover].views === 1 ? '' : 's'}</div>
              <div className="text-cream/60 mono text-[9px] mt-0.5">{fmt(data[hover].day)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
